import { useContext, useMemo } from "react";
import type { Lod } from "design-scope";
import { ProjectContext } from "context/ProjectProvider";
import { useCIQQuery } from "hooks/ciq-gql-hooks";
import type { DesignScopeLodEnumValue } from "pages/project-setting-menu/module-settings/design-scope-settings/model";
import { GET_PROJECT_DESIGN_SCOPE_LEVEL_OF_DETAIL } from "pages/project-setting-menu/module-settings/design-scope-settings/queries";

/** One project LOD from `get_project_design_scope_level_of_detail`. */
export interface LevelOfDetailOption {
  id: string;
  /** Enum `value` (the name shown in settings). */
  label: string;
  /** Enum `level_code`, e.g. "L3". Empty when the API omits it. */
  level?: string;
  description?: string;
  isDefault?: boolean;
  sequenceNo?: number;
}

export interface LevelOfDetailResult {
  options: LevelOfDetailOption[];
  /** Id of the enum value flagged `is_default`, or "" when none is flagged. */
  defaultId: string;
  loading: boolean;
}

type ProjectLodQueryData = {
  get_project_design_scope_level_of_detail?: {
    enum_values?: DesignScopeLodEnumValue[] | null;
  } | null;
};

export function mapLodEnumValuesToOptions(
  enumValues: DesignScopeLodEnumValue[] | null | undefined
): { options: LevelOfDetailOption[]; defaultId: string } {
  const sorted = [...(enumValues ?? [])].sort(
    (a, b) => a.sequence_no - b.sequence_no
  );
  const options = sorted.map((item) => ({
    id: String(item.id),
    label: (item.value ?? "").trim() || String(item.id),
    level: (item.level_code ?? "").trim(),
    description: item.description ?? "",
    isDefault: Boolean(item.is_default),
    sequenceNo: item.sequence_no
  }));
  const defaultItem = sorted.find((item) => item.is_default);
  return {
    options,
    defaultId: defaultItem ? String(defaultItem.id) : ""
  };
}

/**
 * The LOD to preselect: the API default when one is flagged, otherwise the
 * middle option so callers that only have id/label still get a stable choice.
 */
export function defaultLodIdFromOptions(
  options: LevelOfDetailOption[]
): string {
  const flagged = options.find((option) => option.isDefault);
  if (flagged) return flagged.id;
  if (!options.length) return "";
  return options[Math.floor((options.length - 1) / 2)]!.id;
}

/** 1–5 rank for meters, from level code, then sequence, then list position. */
export function lodRankFromOption(
  opt: LevelOfDetailOption,
  index: number
): Lod {
  const fromLevel = (opt.level ?? "").match(/([1-5])/);
  if (fromLevel) return Number(fromLevel[1]) as Lod;
  if (typeof opt.sequenceNo === "number" && opt.sequenceNo >= 1) {
    return Math.min(5, Math.max(1, Math.trunc(opt.sequenceNo))) as Lod;
  }
  return Math.min(5, Math.max(1, index + 1)) as Lod;
}

export function lodForOptionId(
  options: LevelOfDetailOption[],
  id: string
): Lod {
  const index = options.findIndex((option) => option.id === id);
  if (index < 0) return 1;
  return lodRankFromOption(options[index]!, index);
}

/** Bold prefix in the LOD dropdown: API level code, or `L{rank}`. */
export function lodOptionHeading(
  opt: LevelOfDetailOption,
  index: number
): string {
  const level = opt.level?.trim();
  if (level) return level;
  return `L${lodRankFromOption(opt, index)}`;
}

export function useLevelOfDetail(
  projectId: string | undefined
): LevelOfDetailResult {
  const { gqlClientForProject } = useContext(ProjectContext);
  const { data, loading } = useCIQQuery<ProjectLodQueryData>(
    GET_PROJECT_DESIGN_SCOPE_LEVEL_OF_DETAIL,
    {
      client: gqlClientForProject,
      skip: !gqlClientForProject || !projectId
    }
  );

  return useMemo(() => {
    const mapped = mapLodEnumValuesToOptions(
      data?.get_project_design_scope_level_of_detail?.enum_values
    );
    return {
      options: mapped.options,
      defaultId: mapped.defaultId,
      loading
    };
  }, [data, loading]);
}
